/**
 * Global Settings Composable - Provides access to site-wide global settings
 * Uses the GO Commerce GraphQL Cache system for optimized caching
 */
import type { MaintenanceType } from "@/types";
import { executeGraphQLQuery } from "../utils/graphqlCache";

export function useMaintenance() {
  const mainStore = useMainStore();

  // Initialize with data from store if available
  const initialMaintenance = mainStore.getMaintenance || null;
  const maintenance = useState<MaintenanceType | null>(
    "maintenance",
    () => initialMaintenance
  );
  const isLoading = ref(false);

  // GraphQL query for maintenance settings
  const MAINTENANCE_QUERY = `
    query GetMaintenance {
       maintenanceSetting {
          documentId
          enabled
          startDate
          endDate
          maintenancePageSlug
          pages {
            area
          }
        }
    }
  `;

  /**
   * Fetch maintenance settings using the GO Commerce GraphQL Cache system
   * The GraphQL cache handles caching with proper TTLs and shared request prevention
   */
  const fetchMaintenance = async (forceFresh = false) => {
    // Check store first if we have fresh data
    const storeMaintenance = mainStore.getMaintenance;
    if (storeMaintenance && !forceFresh) {
      maintenance.value = storeMaintenance;
      return storeMaintenance;
    }

    // Prevent multiple concurrent API calls
    if (isLoading.value) {
      // Wait for the current fetch to complete
      await new Promise<void>((resolve) => {
        const checkLoading = () => {
          if (!isLoading.value) {
            resolve();
          } else {
            setTimeout(checkLoading, 50);
          }
        };
        checkLoading();
      });
      return maintenance.value;
    }

    try {
      isLoading.value = true;

      // Define the response type
      type ResponseType = {
        maintenanceSetting: MaintenanceType;
      };

      // Execute the GraphQL query using the GO Commerce GraphQL Cache system
      // It automatically handles caching with TTLs and prevents duplicate requests
      const result = await executeGraphQLQuery<ResponseType>(
        {
          query: MAINTENANCE_QUERY,
          operationName: "GetMaintenance",
        },
        30 * 60 * 1000,
        true
      ); // 30 minutes cache TTL, or force fresh if requested

      if (result?.maintenanceSetting) {
        // Store in local state and main store
        maintenance.value = result.maintenanceSetting;
        mainStore.updateMaintenance(result.maintenanceSetting);
        console.log(
          "[useMaintenance] Fetched maintenance settings " +
            (forceFresh ? "(forced fresh)" : "(from cache)")
        );
        return result.maintenanceSetting;
      }

      return null;
    } catch (error) {
      console.error("Error fetching maintenance settings:", error);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    maintenance,
    isLoading: readonly(isLoading),
    fetchMaintenance,
  };
}
